import React, { useState } from "react"
import {
	Avatar,
	Chip,
	CircularProgress,
	Grid,
	Typography,
	Button,
	Link,
	Divider as MuiDivider
} from "@material-ui/core"
import { AvatarGroup } from "@material-ui/lab"
import {
	Phone as PhoneIcon,
	Email as EmailIcon
} from "@material-ui/icons"
import { parsePhoneNumber } from "libphonenumber-js"

import useStyles from "@/components/Messages/ContactCardMessage/styles"

import { IMessageExtraData, ChannelType } from "@/protocols/channel"

import {
	ActionDialog,
	Notification,
	Portlet
} from "@/components"

import ApiService from "@/services/Api"
import useSocket from "@/hooks/useSocket"
import { buildChatLink } from "@/utils/chat"
import { useGlobalStateStore } from "@/store/GlobalState"
import { keepOnlyDigits } from "@/utils/phone"
import { errorCodeToMessage } from "@/utils/startChat"
import { colors } from "@/styles/colors"

type ContactCardMessageProps = {
	extraData: IMessageExtraData
}

const MAX_AVATARS = 3

export const getContactCardDescription = (extraData: IMessageExtraData): string => {
	const totalContacts = extraData?.contactCard?.items?.length || 0

	const descriptionParams = []

	const firstContact = extraData?.contactCard?.items[0]
	descriptionParams.push(firstContact?.name)

	const isThereMoreThanOneContact = totalContacts > 1

	if (isThereMoreThanOneContact) {
		const pendingContacts = totalContacts - 1

		const extraContactsDescription = `e ${pendingContacts} ${pendingContacts === 1 ? "outro contato" : "outros contatos"}`

		descriptionParams.push(extraContactsDescription)
	}

	const description = descriptionParams.join(" ")

	return description
}

export const ContactCardMessageContent: React.FC<ContactCardMessageProps> = (props) => {
	const {
		extraData
	} = props

	const classes = useStyles()

	return (
		<Grid
			container
			alignItems="center"
			spacing={2}
			className={classes.contentContainer}
		>
			<Grid
				item
			>
				<AvatarGroup>
					{extraData?.contactCard?.items
						?.slice(0, MAX_AVATARS)
						?.map((item, index) => (
							<Avatar
								key={index}
								variant="circle"
								src={item.pictureUrl}
								className={classes.avatar}
							/>
						))}
				</AvatarGroup>
			</Grid>

			<Grid
				item
				className={classes.descriptionContainer}
			>
				<Typography
					variant="h2"
					color="textPrimary"
				>
					{getContactCardDescription(extraData)}
				</Typography>
			</Grid>
		</Grid>
	)
}

export const ContactCardMessageContentFooter: React.FC<ContactCardMessageProps> = (props) => {
	const {
		extraData
	} = props

	const classes = useStyles()
	const globalStateStore = useGlobalStateStore()
	const socket = useSocket()

	const [showContactsDialogOpened, setShowContactsDialogOpened] = useState(false)
	const [loadingPhone, setLoadingPhone] = useState<string | null>(null)

	const channelTypeToContactConfig: Record<string, {
		icon: React.ReactElement
		chipLabel: string
		chipStyle: { backgroundColor: string; color: string; fontSize: number }
		canInitiateConversation: boolean
	}> = {
		whatsapp: {
			icon: <PhoneIcon fontSize="small" style={{ color: colors.green[600] }} />,
			chipLabel: "Celular",
			chipStyle: {
				backgroundColor: colors.green[50],
				color: colors.green[700],
				fontSize: 11
			},
			canInitiateConversation: true
		},
		waba: {
			icon: <PhoneIcon fontSize="small" style={{ color: colors.green[600] }} />,
			chipLabel: "Celular",
			chipStyle: {
				backgroundColor: colors.green[50],
				color: colors.green[700],
				fontSize: 11
			},
			canInitiateConversation: true
		},
		email: {
			icon: <EmailIcon fontSize="small" style={{ color: colors.blue[500] }} />,
			chipLabel: "E-mail",
			chipStyle: {
				backgroundColor: colors.blue[50],
				color: colors.blue[700],
				fontSize: 11
			},
			canInitiateConversation: false
		}
	}

	const handleInitiateConversation = async (name: string, phone: string) => {
		if (!globalStateStore.isCurrentChannelConnected) {
			Notification.error({ message: errorCodeToMessage.InboxChannelIsInactive })
			return
		}

		setLoadingPhone(phone)

		try {
			const digitsOnlyPhone = keepOnlyDigits(phone)
			const currentChannel = globalStateStore.currentChannel

			const parsedPhone = parsePhoneNumber(`+${digitsOnlyPhone}`)
			const phoneCountryCode = parsedPhone?.country ?? "BR"

			const { data } = await ApiService.post("/clients/get-or-create", {
				channel_type: currentChannel?.channelType,
				nickname: name,
				contactData: {
					phone: digitsOnlyPhone,
					countryCode: phoneCountryCode
				}
			})

			const client = data.client
			const whatsappContact = client?.contacts?.find(
				(contact: { channel_type: string; id: number }) => contact.channel_type === currentChannel?.channelType
			)

			if (!whatsappContact) {
				Notification.error({ message: errorCodeToMessage.NoValidContactFound })
				return
			}

			const chat = await socket.startChat({
				channelType: currentChannel?.channelType as ChannelType,
				clientId: client.id,
				contactId: whatsappContact.id,
				inboxChannelId: currentChannel?.id as number
			})

			if (!chat) {
				Notification.error({ message: errorCodeToMessage.Default })
				return
			}

			const isAttendedByAnotherUser =
				Boolean(chat?.attendance?.userId) &&
				chat.attendance.userId !== globalStateStore?.user?.id

			if (isAttendedByAnotherUser) {
				Notification.error({ message: errorCodeToMessage.ClientHasChatInProgressWithAnotherAttendant })
				return
			}

			window.open(buildChatLink(client.id), "_blank")
		} catch {
			Notification.error({ message: errorCodeToMessage.Default })
		} finally {
			setLoadingPhone(null)
		}
	}

	const handleOpenShowContactsDialog = () => {
		setShowContactsDialogOpened(true)
	}

	const handleCloseShowContactsDialog = () => {
		setShowContactsDialogOpened(false)
	}

	const totalContacts = extraData?.contactCard?.items?.length
	const contactText = totalContacts === 1 ? "contato" : "contatos"

	return (
		<Grid
			container
		>
			<Link
				component={Button}
				fullWidth
				className={classes.showContactsButton}
				onClick={handleOpenShowContactsDialog}
			>
				Ver {contactText}
			</Link>

			<ActionDialog
				title={contactText.toUpperCase()}
				hideCloseButton
				onClose={handleCloseShowContactsDialog}
				openDialog={showContactsDialogOpened}
				maxWidth="sm"
				fullWidth
			>
				<Grid container spacing={2}>
					{extraData?.contactCard?.items?.map((item, index) => (
						<Grid item xs={12} key={index}>
							<Portlet>
								<Grid container spacing={2}>
									<Grid item xs={12}>
										<Grid container alignItems="center" spacing={2}>
											<Grid item>
												<Avatar
													variant="circle"
													src={item.pictureUrl}
													className={classes.dialogAvatar}
												/>
											</Grid>

											<Grid item className={classes.descriptionContainer}>
												<Typography variant="h2" color="textPrimary">
													{item.name}
												</Typography>
											</Grid>
										</Grid>
									</Grid>

									{item?.contacts?.length > 0 && (
										<Grid item xs={12}>
											<MuiDivider />
										</Grid>
									)}

									{item?.contacts?.map((contact, contactIndex) => {
										const contactConfig = channelTypeToContactConfig[contact.channelType]

										return (
											<React.Fragment key={contactIndex}>
												{contactIndex > 0 && (
													<Grid item xs={12}>
														<MuiDivider />
													</Grid>
												)}

												<Grid item xs={12}>
													<Grid container alignItems="center" spacing={1}>
														<Grid item>
															{contactConfig?.icon}
														</Grid>

														<Grid item className={classes.descriptionContainer}>
															<Typography variant="body2" color="textPrimary">
																{contact.value}
															</Typography>
														</Grid>

														<Grid item>
															<Chip
																size="small"
																label={contactConfig?.chipLabel}
																style={contactConfig?.chipStyle}
															/>
														</Grid>
													</Grid>
												</Grid>

												{contactConfig?.canInitiateConversation && (
													<Grid item xs={12}>
														<Button
															variant="contained"
															color="primary"
															fullWidth
															disabled={loadingPhone === contact.value}
															endIcon={loadingPhone === contact.value && <CircularProgress size={20} />}
															onClick={() => handleInitiateConversation(item.name, contact.value)}
														>
															Iniciar conversa
														</Button>
													</Grid>
												)}
											</React.Fragment>
										)
									})}
								</Grid>
							</Portlet>
						</Grid>
					))}
				</Grid>
			</ActionDialog>
		</Grid>
	)
}
